"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.InternetStatusManager = exports.InternetStatus = void 0;
const CONNECTION_CHECK_TIMEOUT = 2000;
const POLLING_INTERVAL = 10000;
const POLLING_CHECK_INTERVAL = 1000;
const SUCCESSIVE_ONLINE_THRESHOLD = 3;
const SUCCESSIVE_OFFLINE_THRESHOLD = 5;
class InternetStatus {
    constructor(_status) {
        this._status = _status;
    }
    get status() {
        return this._status;
    }
    static from(statusName) {
        const statusMap = {
            UNKNOWN: Unknown,
            ONLINE: Online,
            OFFLINE: Offline,
        };
        return statusMap[statusName];
    }
}
exports.InternetStatus = InternetStatus;
const Unknown = new InternetStatus("UNKNOWN");
const Online = new InternetStatus("ONLINE");
const Offline = new InternetStatus("OFFLINE");
/*
  What different signal detectors can signal,

  1. Browser widow listner
    -> possiblyOnline
    -> offline

  2. Socket connection/disconnection
    -> possiblyOnline
    -> possiblyOffline

  3. Axios response interceptor
    -> possiblyOffline - we get the same response (ERR_NETWORK) for socket & internet disconnection

  4. Internet status poller (internal)
    -> online
    -> offline

  Do nothing - status transitions
    1. Same status -> status
    2. online -> possiblyOnline
    3. offline -> possiblyOffline
*/
class InternetStatusManager {
    constructor(connectionCheckUrl) {
        this.connectionCheckUrl = connectionCheckUrl;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        this.listeners = new Set();
        this.isPollTillConnection = false;
        this.isCheckingAndUpdatingInternetStatus = false;
        this.setStatus(Unknown);
    }
    checkInternetConnection() {
        return __awaiter(this, arguments, void 0, function* (timeout = CONNECTION_CHECK_TIMEOUT) {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), timeout);
            try {
                const response = yield fetch(this.connectionCheckUrl, {
                    method: "HEAD",
                    cache: "no-cache",
                    signal: controller.signal,
                });
                return response.ok;
            }
            catch (_a) {
                return false;
            }
            finally {
                clearTimeout(timeoutId);
            }
        });
    }
    isOffline() {
        return this.internetStatus === Offline;
    }
    confirmInternetStatus() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isCheckingAndUpdatingInternetStatus) {
                return;
            }
            this.isCheckingAndUpdatingInternetStatus = true;
            let successiveOnlineCount = 0;
            let successiveOfflineCount = 0;
            try {
                // eslint-disable-next-line no-constant-condition
                while (true) {
                    const isConnected = yield this.checkInternetConnection(CONNECTION_CHECK_TIMEOUT);
                    if (isConnected) {
                        successiveOnlineCount++;
                        successiveOfflineCount = 0;
                    }
                    else {
                        successiveOfflineCount++;
                        successiveOnlineCount = 0;
                    }
                    if (successiveOfflineCount >= SUCCESSIVE_OFFLINE_THRESHOLD) {
                        this.setStatus(Offline);
                        return;
                    }
                    if (successiveOnlineCount >= SUCCESSIVE_ONLINE_THRESHOLD) {
                        this.setStatus(Online);
                        return;
                    }
                    yield this.sleep(POLLING_CHECK_INTERVAL);
                }
            }
            finally {
                this.isCheckingAndUpdatingInternetStatus = false;
            }
        });
    }
    pollTillConnection() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isPollTillConnection) {
                return;
            }
            this.isPollTillConnection = true;
            if (this.internetPollerIntervalId) {
                return;
            }
            this.internetPollerIntervalId = setInterval(() => __awaiter(this, void 0, void 0, function* () {
                yield this.confirmInternetStatus();
                if (this.isOnline()) {
                    this.clearPollingInterval();
                    this.isPollTillConnection = false;
                }
            }), POLLING_INTERVAL);
        });
    }
    clearPollingInterval() {
        if (this.internetPollerIntervalId) {
            clearInterval(this.internetPollerIntervalId);
            this.internetPollerIntervalId = undefined;
        }
    }
    setStatus(newStatus) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (((_a = this.internetStatus) === null || _a === void 0 ? void 0 : _a.status) === newStatus.status) {
                return;
            }
            this.internetStatus = newStatus;
            this.listeners.forEach(cb => cb(this.isConnected()));
            if (newStatus.status === Unknown.status) {
                this.confirmInternetStatus();
            }
            else if (newStatus.status === Offline.status) {
                this.pollTillConnection();
            }
            else {
                this.clearPollingInterval();
            }
        });
    }
    possiblyOnline() {
        var _a;
        if (((_a = this.internetStatus) === null || _a === void 0 ? void 0 : _a.status) === Online.status) {
            return;
        }
        this.setStatus(Unknown);
    }
    possiblyOffline() {
        var _a;
        if (((_a = this.internetStatus) === null || _a === void 0 ? void 0 : _a.status) === Offline.status) {
            return;
        }
        this.setStatus(Unknown);
    }
    unknown() {
        this.setStatus(Unknown);
    }
    offline() {
        this.setStatus(Offline);
    }
    isConnected() {
        if (!this.internetStatus) {
            return false;
        }
        const connectedStatuses = ["ONLINE", "UNKNOWN"];
        return connectedStatuses.includes(this.internetStatus.status);
    }
    isOnline() {
        var _a;
        return ((_a = this.internetStatus) === null || _a === void 0 ? void 0 : _a.status) === "ONLINE";
    }
    waitForOnline(abortContext) {
        return __awaiter(this, void 0, void 0, function* () {
            while (!this.isOnline()) {
                if (abortContext === null || abortContext === void 0 ? void 0 : abortContext.isAborted)
                    return;
                yield this.sleep(POLLING_CHECK_INTERVAL);
            }
        });
    }
    onChange(cb) {
        this.listeners.add(cb);
        cb(this.isConnected());
        return () => {
            this.listeners.delete(cb);
        };
    }
    startMonitoring(internetStatusListener) {
        this.stopMonitoring();
        this.removeListeners = internetStatusListener(this.offline.bind(this), this.possiblyOnline.bind(this));
    }
    stopMonitoring() {
        if (this.removeListeners) {
            this.removeListeners();
            this.removeListeners = undefined;
        }
        this.clearPollingInterval();
        this.isPollTillConnection = false;
        this.isCheckingAndUpdatingInternetStatus = false;
    }
    sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
}
exports.InternetStatusManager = InternetStatusManager;
